import {forwardRef, MutableRefObject, ReactNode, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {ChevronLeftIconComponent} from '@/components/Icons';
import {interceptProps, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';

interface Props {
  children: ReactNode;
}

export const HorizontalScrollArea = forwardRef<HTMLDivElement, Props>(
  ({children}, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [navigationControl, setNavigationControl] = useState({
      prev: false,
      next: true,
    });

    useEffect(() => {
      if (!ref || !scrollRef.current) return;
      if (typeof ref === 'function') {
        ref(scrollRef.current);
      } else {
        (ref as MutableRefObject<HTMLDivElement>).current = scrollRef.current;
      }
    }, [ref]);

    const handleCheckNavigation = () => {
      if (!scrollRef.current) return;

      const {scrollLeft, clientWidth, scrollWidth} = scrollRef.current;
      const scrollRight = scrollLeft + clientWidth;

      setNavigationControl({
        prev: scrollLeft > 0,
        next: scrollRight < scrollWidth,
      });
    };

    const handleClickNavigation = (direction: 'prev' | 'next') => {
      if (!scrollRef.current) return;
      const {scrollLeft, clientWidth} = scrollRef.current;
      const scrollAmount = direction === 'prev' ? -clientWidth : clientWidth;

      scrollRef.current.scrollTo({
        left: scrollLeft + scrollAmount,
        behavior: 'smooth',
      });
    };

    useEffect(() => {
      handleCheckNavigation();
    }, []);

    return (
      <Root>
        <ScrollArea ref={scrollRef} onScroll={handleCheckNavigation}>
          {children}
        </ScrollArea>
        {navigationControl.prev && (
          <NavButton
            direction="prev"
            onClick={() => handleClickNavigation('prev')}
          >
            <PaddingBox spacing={4}>
              <ChevronLeftIconComponent color={palette.gray600} />
            </PaddingBox>
          </NavButton>
        )}
        {navigationControl.next && (
          <NavButton
            direction="next"
            onClick={() => handleClickNavigation('next')}
          >
            <PaddingBox spacing={4}>
              <ChevronLeftIconComponent color={palette.gray600} />
            </PaddingBox>
          </NavButton>
        )}
      </Root>
    );
  }
);

const Root = styled.div`
  position: relative;
`;

const ScrollArea = styled.div`
  display: flex;
  overflow-x: auto;
  white-space: nowrap;

  -ms-overflow-style: none; /* IE/Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NavButton = styled('div').withConfig({
  shouldForwardProp: interceptProps(['direction']),
})<{
  direction?: 'prev' | 'next';
}>`
  overflow-x: hidden;
  position: absolute;
  top: 0;
  width: 36px;
  height: calc(100% - 1px);
  background: linear-gradient(270deg, transparent 0%, #fff 100%);
  display: flex;
  align-items: center;

  svg {
    display: block;
  }

  ${({direction}) =>
    direction === 'prev' ? 'left: 0;' : 'right: 0; transform: rotate(180deg);'}
`;
